/**
 * POS Utility Functions
 * Helper functions for POS-related operations
 */

/**
 * Formats a currency amount from cents to dollars with 2 decimal places
 * @param {number} cents - Amount in cents
 * @returns {string} - Formatted amount in dollars (e.g., "10.99")
 */
export const formatCurrency = (cents) => {
  return (cents / 100).toFixed(2);
};

/**
 * Generates a detailed transaction description including items, prices, and totals
 * @param {Array} items - Cart items
 * @param {Object} totals - Cart totals including subtotal, taxAmount, and total
 * @param {number} maxLength - Maximum length of the description (default: 1000)
 * @returns {string} - Formatted transaction description
 */
export const generateDetailedDescription = (
  items,
  totals,
  maxLength = 1000
) => {
  // Start with a header
  let description = `POS Sale - ${items.length} items\n\n`;

  // Add each item with its price, but track the length
  const itemDescriptions = [];
  for (const item of items) {
    const itemDesc = `${item.quantity}x ${item.name} @ $${formatCurrency(
      item.unitPrice
    )} = $${formatCurrency(item.totalPrice)}`;
    itemDescriptions.push(itemDesc);
  }

  // Create the footer with totals
  let footer = `\nSubtotal: $${formatCurrency(totals.subtotal)}`;

  // Add discount if applicable
  if (totals.discountAmount && totals.discountAmount > 0) {
    footer += `\nDiscount: -$${formatCurrency(totals.discountAmount)}`;
  }

  footer += `\nTax: $${formatCurrency(totals.taxAmount)}`;
  footer += `\nTotal: $${formatCurrency(totals.total)}`;

  // Calculate how much space we have for item descriptions
  const availableSpace = maxLength - description.length - footer.length;

  // If we don't have enough space for all items, we need to truncate
  if (itemDescriptions.join("\n").length > availableSpace) {
    // Strategy 1: Include as many full items as possible
    let currentLength = 0;
    const includedItems = [];

    for (const itemDesc of itemDescriptions) {
      if (currentLength + itemDesc.length + 1 <= availableSpace) {
        // +1 for newline
        includedItems.push(itemDesc);
        currentLength += itemDesc.length + 1;
      } else {
        break;
      }
    }

    // Add a note about truncation
    if (includedItems.length < itemDescriptions.length) {
      const remainingItems = itemDescriptions.length - includedItems.length;
      const truncationNote = `\n...and ${remainingItems} more item(s)`;

      // Check if we have space for the truncation note
      if (currentLength + truncationNote.length <= availableSpace) {
        description += includedItems.join("\n") + truncationNote;
      } else {
        // If not enough space for the note, just include as many items as possible
        description += includedItems.join("\n");
      }
    } else {
      description += includedItems.join("\n");
    }
  } else {
    // We have enough space for all items
    description += itemDescriptions.join("\n");
  }

  // Add the footer
  description += footer;

  // Final safety check to ensure we're under the limit
  if (description.length > maxLength) {
    description = description.substring(0, maxLength - 3) + "...";
  }

  return description;
};

/**
 * Generates a summarized transaction description for cases with many items
 * @param {Array} items - Cart items
 * @param {Object} totals - Cart totals including subtotal, taxAmount, and total
 * @returns {string} - Formatted transaction description
 */
export const generateSummarizedDescription = (items, totals) => {
  // Group items by category
  const categories = {};
  items.forEach((item) => {
    const category = item.category || "Uncategorized";
    if (!categories[category]) {
      categories[category] = {
        count: 0,
        total: 0,
      };
    }
    categories[category].count += item.quantity;
    categories[category].total += item.totalPrice;
  });

  // Create description
  let description = `POS Sale - ${items.length} items\n\n`;

  // Add category summaries
  Object.keys(categories).forEach((category) => {
    description += `${
      categories[category].count
    }x ${category} items: $${formatCurrency(categories[category].total)}\n`;
  });

  // Add totals
  description += `\nSubtotal: $${formatCurrency(totals.subtotal)}`;

  // Add discount if applicable
  if (totals.discountAmount && totals.discountAmount > 0) {
    description += `\nDiscount: -$${formatCurrency(totals.discountAmount)}`;
  }

  description += `\nTax: $${formatCurrency(totals.taxAmount)}`;
  description += `\nTotal: $${formatCurrency(totals.total)}`;

  return description;
};

/**
 * Selects the appropriate description format based on the number of items
 * @param {Array} items - Cart items
 * @param {Object} totals - Cart totals including subtotal, taxAmount, and total
 * @returns {string} - Formatted transaction description
 */
export const generateSmartDescription = (items, totals) => {
  // If we have more than 10 items, use the summarized description
  if (items.length > 10) {
    return generateSummarizedDescription(items, totals);
  }

  // Otherwise use the detailed description
  return generateDetailedDescription(items, totals);
};
